// ---------------------------------------------------------------------------
// BlockResizeHandles — 8-direction resize handles for absolute-positioned blocks.
//
// Follows the same pattern as selection/resize-handles.tsx but operates on
// block-level AbsoluteBlockPosition (x/y/w/h) instead of element className.
//
// z-index: 51 — same convention as existing ResizeHandles.
// ---------------------------------------------------------------------------

import { useEffect, useRef, useState } from 'react'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import type { AbsoluteBlockPosition } from '@cogo/types-v2'

type Direction = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w'

const HANDLE_SIZE = 8
const HALF = HANDLE_SIZE / 2
const MIN_SIZE = 20

const CURSORS: Record<Direction, string> = {
  nw: 'nwse-resize',
  n: 'ns-resize',
  ne: 'nesw-resize',
  e: 'ew-resize',
  se: 'nwse-resize',
  s: 'ns-resize',
  sw: 'nesw-resize',
  w: 'ew-resize',
}

interface BlockResizeHandlesProps {
  blockName: string
  position: AbsoluteBlockPosition
  width: number
  height: number
}

export function BlockResizeHandles({
  blockName,
  position,
  width,
  height,
}: BlockResizeHandlesProps) {
  // Local visual size during drag
  const [localSize, setLocalSize] = useState({ x: position.x, y: position.y, w: width, h: height })
  const localSizeRef = useRef(localSize)

  // Sync from props when position changes externally
  useEffect(() => {
    const newSize = { x: position.x, y: position.y, w: width, h: height }
    setLocalSize(newSize)
    localSizeRef.current = newSize
  }, [position.x, position.y, width, height])

  const dragRef = useRef<{
    dir: Direction
    startMX: number
    startMY: number
    startX: number
    startY: number
    startW: number
    startH: number
  } | null>(null)

  // Window-level mousemove + mouseup for resize
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragRef.current) return
      e.preventDefault()

      const zoom = useEditorStore.getState().zoom || 1
      const { dir, startMX, startMY, startX, startY, startW, startH } = dragRef.current
      const dx = (e.clientX - startMX) / zoom
      const dy = (e.clientY - startMY) / zoom

      let newX = startX
      let newY = startY
      let newW = startW
      let newH = startH

      // Horizontal resize
      if (dir.includes('e')) {
        newW = Math.max(MIN_SIZE, startW + dx)
      }
      if (dir.includes('w')) {
        const dw = Math.min(dx, startW - MIN_SIZE)
        newW = startW - dw
        newX = startX + dw
      }

      // Vertical resize
      if (dir.includes('s')) {
        newH = Math.max(MIN_SIZE, startH + dy)
      }
      if (dir === 'n' || dir === 'nw' || dir === 'ne') {
        const dh = Math.min(dy, startH - MIN_SIZE)
        newH = startH - dh
        newY = startY + dh
      }

      const newSize = { x: newX, y: newY, w: newW, h: newH }
      setLocalSize(newSize)
      localSizeRef.current = newSize
    }

    const handleMouseUp = () => {
      if (!dragRef.current) return

      // Commit to store
      const store = useEditorStore.getState()
      const activePageId = store.activePageId
      if (activePageId) {
        const s = localSizeRef.current
        store.updateBlockPosition(activePageId, blockName, {
          x: Math.round(s.x),
          y: Math.round(s.y),
          w: Math.round(s.w),
          h: Math.round(s.h),
        })
      }

      dragRef.current = null
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [blockName])

  const handleMouseDown = (e: React.MouseEvent, dir: Direction) => {
    e.preventDefault()
    e.stopPropagation()
    dragRef.current = {
      dir,
      startMX: e.clientX,
      startMY: e.clientY,
      startX: localSizeRef.current.x,
      startY: localSizeRef.current.y,
      startW: localSizeRef.current.w,
      startH: localSizeRef.current.h,
    }
    document.body.style.cursor = CURSORS[dir]
    document.body.style.userSelect = 'none'
  }

  // Handle positions relative to the block (0,0 is top-left of the block)
  const w = localSize.w
  const h = localSize.h

  const handles: { dir: Direction; top: number; left: number }[] = [
    // Corners
    { dir: 'nw', top: -HALF, left: -HALF },
    { dir: 'ne', top: -HALF, left: w - HALF },
    { dir: 'sw', top: h - HALF, left: -HALF },
    { dir: 'se', top: h - HALF, left: w - HALF },
    // Edges
    { dir: 'n', top: -HALF, left: w / 2 - HALF },
    { dir: 's', top: h - HALF, left: w / 2 - HALF },
    { dir: 'w', top: h / 2 - HALF, left: -HALF },
    { dir: 'e', top: h / 2 - HALF, left: w - HALF },
  ]

  return (
    <div
      className="pointer-events-none absolute inset-0"
      style={{ zIndex: 51, overflow: 'visible' }}
      data-testid="block-resize-handles"
    >
      {handles.map((handle) => (
        <div
          key={handle.dir}
          className="absolute rounded-[2px] border border-white bg-blue-500 shadow-sm"
          style={{
            top: handle.top,
            left: handle.left,
            width: HANDLE_SIZE,
            height: HANDLE_SIZE,
            cursor: CURSORS[handle.dir],
            pointerEvents: 'auto',
          }}
          onMouseDown={(e) => handleMouseDown(e, handle.dir)}
          data-testid={`block-resize-handle-${handle.dir}`}
        />
      ))}

      {/* Dimension label — below bottom edge */}
      <div
        className="absolute flex items-center justify-center rounded px-1.5 py-0.5 text-[10px] font-medium leading-none whitespace-nowrap"
        style={{
          top: h + 6,
          left: w / 2,
          transform: 'translateX(-50%)',
          backgroundColor: 'hsl(210 100% 50%)',
          color: '#fff',
          pointerEvents: 'none',
        }}
        data-testid="block-dimension-label"
      >
        {Math.round(w)} x {Math.round(h)}
      </div>
    </div>
  )
}
